// Turns the server-shaped market-data series into the SciChart PricePlot (lines + ±σ bands).
// This is the settles/forwards/vol-smile/tenor drawing logic from the old Highcharts page,
// re-expressed as library-agnostic segments the SciChart PriceChart renders.
import type { MarketDataSeries } from 'pages/pricing/shared/types';
import type { PriceBand, PriceLine, PricePlot } from './price-chart';

const ms = (t: string) => new Date(`${t.slice(0, 10)}T00:00:00Z`).getTime();

const DAY_MS = 86_400_000;
// Year-fraction from the as-of to the tenor (floored at one day so near-dated slices stay finite).
const yearFraction = (asOf: string, tenor: string) =>
  Math.max((ms(tenor) - ms(asOf)) / DAY_MS / 365.25, 1 / 365.25);

interface SviParams {
  a: number | null;
  b: number | null;
  rho: number | null;
  m: number | null;
  sigma: number | null;
}

// Implied vol at log-moneyness k for an SVI slice with time-to-expiry t (years); null if any
// param is missing or total variance is non-positive.
const sviVol = (p: SviParams, k: number, t: number): number | null => {
  const { a, b, rho, m, sigma } = p;
  if (a === null || b === null || rho === null || m === null || sigma === null)
    return null;
  const w = a + b * (rho * (k - m) + Math.sqrt((k - m) ** 2 + sigma ** 2));
  return w > 0 ? Math.sqrt(w / t) : null;
};

// SVI vol surface drawn as iso-moneyness term structures; ATM solid, wings dashed.
const SMILE_LEVELS = [
  { k: -0.2, width: 1 },
  { k: -0.1, width: 1 },
  { k: 0, width: 2 },
  { k: 0.1, width: 1 },
  { k: 0.2, width: 1 },
];

export interface SeriesMeta {
  name: string;
  color: string;
  addRisk: boolean;
}

/**
 * Assemble the SciChart plot from the server series; `metaById` supplies the page-local
 * color / name / risk flag for each series id.
 *
 * @param {MarketDataSeries[]} resSeries - The server-shaped series (settles/forwards/svi/points).
 * @param {Map<number | string, SeriesMeta>} metaById - Page-local meta keyed by series id.
 * @param {string} baseAsOf - The base as-of date (anchors the SVI year-fraction).
 * @return {PricePlot} The line + band segments plus axis chrome for PriceChart.
 */
export function buildPricePlot(
  resSeries: MarketDataSeries[],
  metaById: Map<number | string, SeriesMeta>,
  baseAsOf: string
): PricePlot {
  const lines: PriceLine[] = [];
  const bands: PriceBand[] = [];
  let hasVol = false;

  resSeries.forEach((rs) => {
    const local = metaById.get(rs.id);
    if (!local) return;
    const axis: 'price' | 'vol' = rs.metric === 'vol' ? 'vol' : 'price';
    if (axis === 'vol') hasVol = true;

    // Settle line: one solid point per as-of (front-month settle). Drawn *alongside* the forward
    // strips for an `sf` series — the backend always returns both {settles, forwards}, so this is
    // an independent line, not another arm of the forwards/svi/points chain below.
    if (rs.settles && rs.settles.length > 0) {
      lines.push({
        key: `${rs.id}-settle`,
        name: local.name,
        color: local.color,
        axis,
        points: rs.settles.map((s) => [ms(s.date), s.value]),
      });
    }

    if (rs.forwards) {
      const multi = rs.forwards.length > 1;
      rs.forwards.forEach((strip, i) => {
        lines.push({
          key: `${rs.id}-fwd-${i}`,
          name: local.name,
          color: local.color,
          axis,
          dash: multi && i > 0,
          points: strip.points.map((p) => [ms(p.tenor), p.value]),
        });
        // ±σ risk band (price metric, single strip) drawn from the sigma on each point.
        if (local.addRisk && rs.metric === 'price' && !multi) {
          const env = (sign: number): [number, number][] =>
            strip.points
              .filter((p) => p.sigma !== null)
              .map((p) => [
                ms(p.tenor),
                p.value * (1 + sign * (p.sigma as number)),
              ]);
          bands.push({
            key: `${rs.id}-band`,
            color: local.color,
            hi: env(1),
            lo: env(-1),
          });
        }
      });
    } else if (rs.metric === 'vol' && rs.svi && rs.svi.length > 0) {
      SMILE_LEVELS.forEach((lvl) => {
        const points = (rs.svi ?? [])
          .map((p) => {
            const v = sviVol(p, lvl.k, yearFraction(baseAsOf, p.tenor));
            return v === null
              ? null
              : ([ms(p.tenor), v] as [number, number] | null);
          })
          .filter((pt): pt is [number, number] => pt !== null);
        if (points.length === 0) return;
        lines.push({
          key: `${rs.id}-smile-${lvl.k}`,
          name: local.name,
          color: local.color,
          axis: 'vol',
          dash: lvl.k !== 0,
          width: lvl.width,
          points,
        });
      });
    } else if (rs.points) {
      lines.push({
        key: `${rs.id}-line`,
        name: local.name,
        color: local.color,
        axis,
        points: rs.points.map((p) => [ms(p.tenor), p.value]),
      });
    }
  });

  const allVol = lines.length > 0 && lines.every((l) => l.axis === 'vol');
  return {
    lines,
    bands,
    hasVol,
    priceDec: 2,
    yLabel: allVol ? 'Implied vol' : 'Price',
  };
}
